import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import OperatorLayout from "./OperatorLayout";
import EmailTemplateEditor from "@/components/operator/EmailTemplateEditor";
import UserManagement from "@/components/operator/UserManagement";
import ApiKeyManagement from "@/components/operator/ApiKeyManagement";
import MollieDiagnosticsPanel from "@/components/operator/MollieDiagnosticsPanel";
import AuditLogPanel from "@/components/operator/AuditLogPanel";

interface EmailTemplate {
  label: string;
  description: string;
  subjectKey: string;
  bodyKey: string;
  placeholders: string[];
}

const providerTemplates: EmailTemplate[] = [
  {
    label: "Opportunity-Benachrichtigung",
    description: "E-Mail bei neuer Projektanfrage an den Handwerksbetrieb",
    subjectKey: "provider_opportunity_subject",
    bodyKey: "provider_opportunity_body",
    placeholders: ["{{provider_name}}", "{{category}}", "{{urgency}}", "{{location}}", "{{company}}", "{{portal_link}}", "{{claim_link}}"],
  },
];

const customerTemplates: EmailTemplate[] = [
  {
    label: "Anfrage-Bestätigung",
    description: "Bestätigung nach Eingang einer neuen Anfrage",
    subjectKey: "customer_confirmation_subject",
    bodyKey: "customer_confirmation_body",
    placeholders: ["{{customer_name}}", "{{request_number}}", "{{category}}", "{{urgency}}", "{{status_link}}"],
  },
  {
    label: "Betrieb-Rückfrage",
    description: "Weiterleitung einer Rückfrage vom Handwerksbetrieb an den Endkunden",
    subjectKey: "customer_question_subject",
    bodyKey: "customer_question_body",
    placeholders: ["{{customer_name}}", "{{request_number}}", "{{question_text}}", "{{answer_link}}"],
  },
  {
    label: "Match gefunden",
    description: "Benachrichtigung wenn ein Handwerksbetrieb die Anfrage angenommen hat",
    subjectKey: "customer_match_subject",
    bodyKey: "customer_match_body",
    placeholders: ["{{customer_name}}", "{{request_number}}", "{{provider_company}}", "{{provider_contact}}", "{{category}}"],
  },
  {
    label: "Abschluss & Feedback",
    description: "Feedback-Anfrage nach Abschluss eines Projekts",
    subjectKey: "customer_feedback_subject",
    bodyKey: "customer_feedback_body",
    placeholders: ["{{customer_name}}", "{{request_number}}", "{{provider_company}}", "{{feedback_link}}"],
  },
];

const defaultValues: Record<string, string> = {
  provider_opportunity_subject: "Neue Projektanfrage: {{category}} | {{company}}",
  provider_opportunity_body: "Hallo {{provider_name}},\n\neine neue Anfrage passt zu Ihrem Profil:\n\n• Kategorie: {{category}}\n• Dringlichkeit: {{urgency}}\n• Standort: {{location}}\n\nZum Handwerker-Portal: {{portal_link}}\n\nViele Grüße,\nCRAFTFINDUS",
  customer_confirmation_subject: "Ihre Anfrage {{request_number}} ist eingegangen",
  customer_confirmation_body: "Hallo {{customer_name}},\n\nvielen Dank für Ihre Anfrage.\n\n• Anfrage-Nr.: {{request_number}}\n• Kategorie: {{category}}\n• Dringlichkeit: {{urgency}}\n\nWir suchen jetzt die passenden Handwerksbetriebe für Sie. Sie erhalten eine Benachrichtigung, sobald ein Match gefunden wurde.\n\nStatus verfolgen: {{status_link}}\n\nViele Grüße,\nCRAFTFINDUS",
  customer_question_subject: "Rückfrage zu Ihrer Anfrage {{request_number}}",
  customer_question_body: "Hallo {{customer_name}},\n\nzu Ihrer Anfrage {{request_number}} hat ein Handwerksbetrieb eine Rückfrage:\n\n\"{{question_text}}\"\n\nBitte beantworten Sie die Frage hier: {{answer_link}}\n\nViele Grüße,\nCRAFTFINDUS",
  customer_match_subject: "Match gefunden für Anfrage {{request_number}}",
  customer_match_body: "Hallo {{customer_name}},\n\nfür Ihre Anfrage {{request_number}} wurde ein passender Handwerksbetrieb gefunden:\n\n• Unternehmen: {{provider_company}}\n• Ansprechpartner: {{provider_contact}}\n• Kategorie: {{category}}\n\nSie werden in Kürze kontaktiert.\n\nViele Grüße,\nCRAFTFINDUS",
  customer_feedback_subject: "Wie war Ihre Erfahrung? Anfrage {{request_number}}",
  customer_feedback_body: "Hallo {{customer_name}},\n\nIhre Anfrage {{request_number}} wurde abgeschlossen ({{provider_company}}).\n\nWir würden uns über Ihr Feedback freuen: {{feedback_link}}\n\nViele Grüße,\nCRAFTFINDUS",
};

const OperatorSettings = () => {
  const { t } = useTranslation();
  const [activeSection, setActiveSection] = useState<"email_provider" | "email_customer" | "graph" | "mollie" | "mollie_diagnostics" | "audit" | "users" | "api_keys">("email_provider");
  const [templates, setTemplates] = useState<Record<string, string>>(defaultValues);

  // Mollie settings state
  const [mollieApiKey, setMollieApiKey] = useState("");
  const [mollieProfileId, setMollieProfileId] = useState("");
  const [mollieSaving, setMollieSaving] = useState(false);
  const [mollieLoaded, setMollieLoaded] = useState(false);
  const [mollieTestResult, setMollieTestResult] = useState<null | { ok: boolean; message: string }>(null);
  const [mollieTesting, setMollieTesting] = useState(false);

  // Graph settings state
  const [graphTenantId, setGraphTenantId] = useState("");
  const [graphClientId, setGraphClientId] = useState("");
  const [graphClientSecret, setGraphClientSecret] = useState("");
  const [graphSenderEmail, setGraphSenderEmail] = useState("");
  const [retryAttempts, setRetryAttempts] = useState(3);
  const [retryIntervals, setRetryIntervals] = useState("5,10");
  const [graphSaving, setGraphSaving] = useState(false);
  const [graphLoaded, setGraphLoaded] = useState(false);
  const [showTestDialog, setShowTestDialog] = useState(false);
  const [testEmail, setTestEmail] = useState("");
  const [testSending, setTestSending] = useState(false);

  // Load Mollie settings from matching_config
  useEffect(() => {
    if (mollieLoaded) return;
    supabase
      .from("matching_config")
      .select("config_key, config_value")
      .in("config_key", ["mollie_api_key", "mollie_profile_id"])
      .then(({ data }) => {
        if (data) {
          for (const row of data) {
            const val = typeof row.config_value === "string" ? row.config_value : (row.config_value as any)?.value || "";
            if (row.config_key === "mollie_api_key") setMollieApiKey(val);
            if (row.config_key === "mollie_profile_id") setMollieProfileId(val);
          }
        }
        setMollieLoaded(true);
      });
  }, [mollieLoaded]);

  // Load Graph settings from matching_config
  useEffect(() => {
    if (graphLoaded) return;
    supabase
      .from("matching_config")
      .select("config_key, config_value")
      .in("config_key", ["graph_tenant_id", "graph_client_id", "graph_client_secret", "graph_sender_email", "graph_retry_attempts", "graph_retry_intervals"])
      .then(({ data }) => {
        if (data) {
          for (const row of data) {
            const val = typeof row.config_value === "string" ? row.config_value : (row.config_value as any)?.value || "";
            switch (row.config_key) {
              case "graph_tenant_id": setGraphTenantId(val); break;
              case "graph_client_id": setGraphClientId(val); break;
              case "graph_client_secret": setGraphClientSecret(val); break;
              case "graph_sender_email": setGraphSenderEmail(val); break;
              case "graph_retry_attempts": setRetryAttempts(Number(val) || 3); break;
              case "graph_retry_intervals": setRetryIntervals(val || "5,10"); break;
            }
          }
        }
        setGraphLoaded(true);
      });
  }, [graphLoaded]);

  const updateTemplate = (key: string, value: string) => {
    setTemplates((prev) => ({ ...prev, [key]: value }));
  };

  const handleSaveEmailTemplates = () => {
    toast.success(t("op.setTemplatesSaved"));
  };

  const { user } = useAuth();

  const handleSaveGraphSettings = async () => {
    setGraphSaving(true);
    try {
      const entries = [
        { config_key: "graph_tenant_id", config_value: { value: graphTenantId } },
        { config_key: "graph_client_id", config_value: { value: graphClientId } },
        { config_key: "graph_client_secret", config_value: { value: graphClientSecret } },
        { config_key: "graph_sender_email", config_value: { value: graphSenderEmail } },
        { config_key: "graph_retry_attempts", config_value: { value: String(retryAttempts) } },
        { config_key: "graph_retry_intervals", config_value: { value: retryIntervals } },
      ];
      for (const entry of entries) {
        const { error } = await supabase
          .from("matching_config")
          .upsert(
            { ...entry, updated_by: user?.id || null, updated_at: new Date().toISOString() },
            { onConflict: "config_key" }
          );
        if (error) throw error;
      }
      toast.success(t("op.setGraphSaved"));
    } catch (err: any) {
      toast.error(err.message || t("common.error"));
    } finally {
      setGraphSaving(false);
    }
  };

  const handleSaveMollieSettings = async () => {
    setMollieSaving(true);
    try {
      const entries = [
        { config_key: "mollie_api_key", config_value: { value: mollieApiKey } },
        { config_key: "mollie_profile_id", config_value: { value: mollieProfileId } },
      ];
      for (const entry of entries) {
        const { error } = await supabase
          .from("matching_config")
          .upsert(
            { ...entry, updated_by: user?.id || null, updated_at: new Date().toISOString() },
            { onConflict: "config_key" }
          );
        if (error) throw error;
      }
      toast.success(t("op.setMollieSaved"));
    } catch (err: any) {
      toast.error(err.message || t("common.error"));
    } finally {
      setMollieSaving(false);
    }
  };

  const handleTestMollieConnection = async () => {
    setMollieTesting(true);
    setMollieTestResult(null);
    try {
      const { data, error } = await supabase.functions.invoke("mollie-api", {
        body: { action: "test_connection" },
      });
      if (error) throw error;
      setMollieTestResult({ ok: data.ok, message: data.message });
    } catch (err: any) {
      setMollieTestResult({ ok: false, message: err.message || t("op.setMollieTestFail") });
    } finally {
      setMollieTesting(false);
    }
  };

  const sections = [
    { id: "email_provider" as const, label: t("op.setProviderTemplates") },
    { id: "email_customer" as const, label: t("op.setCustomerTemplates") },
    { id: "graph" as const, label: t("op.setGraph") },
    { id: "mollie" as const, label: "Mollie" },
    { id: "mollie_diagnostics" as const, label: t("op.setMollieDiag") },
    { id: "audit" as const, label: t("op.navAudit") },
    { id: "users" as const, label: t("op.setUsers") },
    { id: "api_keys" as const, label: t("op.setApiKeys") },
  ];

  const renderTemplateGroup = (templateList: EmailTemplate[]) => (
    <div className="space-y-6">
      {templateList.map((tpl) => (
        <EmailTemplateEditor
          key={tpl.subjectKey}
          label={tpl.label}
          description={tpl.description}
          subjectKey={tpl.subjectKey}
          bodyKey={tpl.bodyKey}
          placeholders={tpl.placeholders}
          subjectValue={templates[tpl.subjectKey] || ""}
          bodyValue={templates[tpl.bodyKey] || ""}
          onSubjectChange={(v) => updateTemplate(tpl.subjectKey, v)}
          onBodyChange={(v) => updateTemplate(tpl.bodyKey, v)}
        />
      ))}

      <Button variant="commit" onClick={handleSaveEmailTemplates}>
        {t("op.setSaveTemplates")}
      </Button>
    </div>
  );

  return (
    <OperatorLayout title={t("op.setTitle")} subtitle={t("op.setSubtitle")}>
      <div className="flex gap-8">
        {/* Section Nav */}
        <div className="w-48 shrink-0 space-y-1">
          {sections.map((section) => (
            <button
              key={section.id}
              onClick={() => setActiveSection(section.id)}
              className={cn(
                "w-full text-left px-4 py-2.5 text-sm transition-colors border-l-2",
                activeSection === section.id
                  ? "border-accent text-foreground bg-accent/5"
                  : "border-transparent text-muted-foreground hover:text-foreground"
              )}
            >
              {section.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 max-w-2xl">
          {activeSection === "email_provider" && renderTemplateGroup(providerTemplates)}
          {activeSection === "email_customer" && renderTemplateGroup(customerTemplates)}

          {activeSection === "graph" && (
            <div className="space-y-8">
              <div className="p-6 border border-border bg-card space-y-6">
                <div>
                  <h3 className="text-subheadline mb-1">{t("op.setGraphTitle")}</h3>
                  <p className="text-caption">{t("op.setGraphDesc")}</p>
                </div>

                <div>
                  <label className="text-micro block mb-2">{t("op.setTenantId")}</label>
                  <input
                    type="text"
                    value={graphTenantId}
                    onChange={(e) => setGraphTenantId(e.target.value)}
                    placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                    className="w-full h-10 px-4 border border-border bg-background text-body focus:outline-none focus:border-primary"
                  />
                </div>

                <div>
                  <label className="text-micro block mb-2">{t("op.setClientId")}</label>
                  <input
                    type="text"
                    value={graphClientId}
                    onChange={(e) => setGraphClientId(e.target.value)}
                    placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                    className="w-full h-10 px-4 border border-border bg-background text-body focus:outline-none focus:border-primary"
                  />
                </div>

                <div>
                  <label className="text-micro block mb-2">{t("op.setClientSecret")}</label>
                  <input
                    type="password"
                    value={graphClientSecret}
                    onChange={(e) => setGraphClientSecret(e.target.value)}
                    placeholder="••••••••••••••••"
                    className="w-full h-10 px-4 border border-border bg-background text-body focus:outline-none focus:border-primary"
                  />
                </div>

                <div>
                  <label className="text-micro block mb-2">{t("op.setSenderEmail")}</label>
                  <input
                    type="email"
                    value={graphSenderEmail}
                    onChange={(e) => setGraphSenderEmail(e.target.value)}
                    placeholder="noreply@craftfindus.de"
                    className="w-full h-10 px-4 border border-border bg-background text-body focus:outline-none focus:border-primary"
                  />
                </div>

                <div className="divider-line" />

                <div>
                  <h4 className="text-body font-medium mb-4">{t("op.setRetryPolicy")}</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-micro block mb-2">{t("op.setMaxAttempts")}</label>
                      <input
                        type="number"
                        value={retryAttempts}
                        onChange={(e) => setRetryAttempts(Number(e.target.value))}
                        min={1}
                        max={10}
                        className="w-full h-10 px-4 border border-border bg-background text-body focus:outline-none focus:border-primary"
                      />
                    </div>
                    <div>
                      <label className="text-micro block mb-2">{t("op.setIntervals")}</label>
                      <input
                        type="text"
                        value={retryIntervals}
                        onChange={(e) => setRetryIntervals(e.target.value)}
                        placeholder="5,10"
                        className="w-full h-10 px-4 border border-border bg-background text-body focus:outline-none focus:border-primary"
                      />
                      <p className="text-micro mt-1">{t("op.setIntervalsHint")}</p>
                    </div>
                  </div>
                </div>

                <div className="flex gap-3">
                  <Button variant="commit" onClick={handleSaveGraphSettings} disabled={graphSaving}>
                    {graphSaving ? t("op.setSavingGraph") : t("op.setSaveGraph")}
                  </Button>
                  <Button
                    variant="quiet"
                    onClick={() => { setTestEmail(""); setShowTestDialog(true); }}
                    disabled={graphSaving}
                  >
                    {t("op.setTestEmail")}
                  </Button>
                </div>
              </div>
            </div>
          )}

          {activeSection === "mollie" && (
            <div className="space-y-8">
              <div className="p-6 border border-border bg-card space-y-6">
                <div>
                  <h3 className="text-subheadline mb-1">{t("op.setMollieTitle")}</h3>
                  <p className="text-caption">{t("op.setMollieDesc")}</p>
                </div>

                <div>
                  <label className="text-micro block mb-2">{t("op.setMollieApiKey")}</label>
                  <input
                    type="password"
                    value={mollieApiKey}
                    onChange={(e) => setMollieApiKey(e.target.value)}
                    placeholder="test_xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                    className="w-full h-10 px-4 border border-border bg-background text-body focus:outline-none focus:border-primary font-mono"
                  />
                  <p className="text-micro mt-1 text-muted-foreground">{t("op.setMollieKeyHint")}</p>
                </div>

                <div>
                  <label className="text-micro block mb-2">{t("op.setMollieProfileId")}</label>
                  <input
                    type="text"
                    value={mollieProfileId}
                    onChange={(e) => setMollieProfileId(e.target.value)}
                    placeholder="pfl_xxxxxxxx"
                    className="w-full h-10 px-4 border border-border bg-background text-body focus:outline-none focus:border-primary font-mono"
                  />
                  <p className="text-micro mt-1 text-muted-foreground">{t("op.setMollieProfileHint")}</p>
                </div>

                {mollieTestResult && (
                  <div className={cn(
                    "p-4 border text-sm",
                    mollieTestResult.ok
                      ? "border-green-300 bg-green-50 text-green-800"
                      : "border-red-300 bg-red-50 text-red-800"
                  )}>
                    {mollieTestResult.ok ? "✅ " : "❌ "}{mollieTestResult.message}
                  </div>
                )}

                <div className="flex gap-3">
                  <Button variant="commit" onClick={handleSaveMollieSettings} disabled={mollieSaving}>
                    {mollieSaving ? t("op.setMollieSaving") : t("op.setMollieSave")}
                  </Button>
                  <Button
                    variant="quiet"
                    onClick={handleTestMollieConnection}
                    disabled={mollieTesting || !mollieApiKey}
                  >
                    {mollieTesting ? t("op.setMollieTesting") : t("op.setMollieTestBtn")}
                  </Button>
                </div>
              </div>
            </div>
          )}

          {activeSection === "mollie_diagnostics" && <MollieDiagnosticsPanel />}
          {activeSection === "audit" && <AuditLogPanel />}
          {activeSection === "users" && <UserManagement />}
          {activeSection === "api_keys" && <ApiKeyManagement />}
        </div>
      </div>
      {/* Test E-Mail Dialog */}
      {showTestDialog && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-foreground/20 backdrop-blur-sm">
          <div className="bg-card border border-border p-8 w-full max-w-md space-y-6">
            <h2 className="text-subheadline">{t("op.setTestTitle")}</h2>
            <p className="text-caption">{t("op.setTestDesc")}</p>
            <div>
              <label className="text-micro block mb-2">{t("op.setTestRecipient")}</label>
              <input
                type="email"
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
                placeholder="empfaenger@beispiel.de"
                className="w-full h-10 px-4 border border-border bg-background text-body focus:outline-none focus:border-primary"
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === "Enter" && testEmail.includes("@")) {
                    e.preventDefault();
                    document.getElementById("btn-send-test")?.click();
                  }
                }}
              />
            </div>
            <div className="flex gap-3">
              <Button
                id="btn-send-test"
                variant="commit"
                className="flex-1"
                disabled={testSending || !testEmail.includes("@")}
                onClick={async () => {
                  setTestSending(true);
                  try {
                    const { data, error } = await supabase.functions.invoke("graph-test-email", {
                      body: { to_email: testEmail },
                    });
                    if (error) {
                      toast.error(error.message || t("common.error"));
                    } else if (data?.success) {
                      toast.success(t("op.setTestSent", { email: testEmail }));
                      setShowTestDialog(false);
                    } else {
                      toast.error(data?.error || t("common.error"));
                    }
                  } catch (e: any) {
                    toast.error(e.message);
                  } finally {
                    setTestSending(false);
                  }
                }}
              >
                {testSending ? t("op.setTestSending") : t("op.setTestSend")}
              </Button>
              <Button
                variant="quiet"
                onClick={() => setShowTestDialog(false)}
                disabled={testSending}
              >
                {t("common.cancel")}
              </Button>
            </div>
          </div>
        </div>
      )}
    </OperatorLayout>
  );
};

export default OperatorSettings;
